import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Router, Server, Database, Wifi, Shield } from 'lucide-react';
import { cn } from '../../lib/utils';

interface DeviceDiscoveryAnimationProps {
  isLoading: boolean;
  onComplete?: () => void;
  className?: string;
}

const deviceTypes = [
  { icon: Router, name: 'Router', color: 'text-blue-500' },
  { icon: Server, name: 'Server', color: 'text-green-500' },
  { icon: Database, name: 'Database', color: 'text-purple-500' },
  { icon: Wifi, name: 'Access Point', color: 'text-amber-500' },
  { icon: Shield, name: 'Firewall', color: 'text-red-500' },
];

export function DeviceDiscoveryAnimation({ 
  isLoading, 
  onComplete,
  className
}: DeviceDiscoveryAnimationProps) {
  const [currentStage, setCurrentStage] = useState(0);
  const [discoveredDevices, setDiscoveredDevices] = useState<number[]>([]);
  const messages = [
    'Initializing network scan...',
    'Scanning for devices...',
    'Identifying device types...',
    'Gathering device information...',
    'Finalizing discovery...'
  ];

  useEffect(() => {
    if (!isLoading) {
      if (onComplete) onComplete();
      return;
    }

    // Progress through stages
    const interval = setInterval(() => {
      if (currentStage < 4) {
        setCurrentStage(prev => prev + 1);
      } else {
        clearInterval(interval);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [isLoading, currentStage, onComplete]);

  useEffect(() => {
    if (!isLoading) return;

    // Add random devices periodically
    const deviceInterval = setInterval(() => {
      const randomDeviceIndex = Math.floor(Math.random() * deviceTypes.length);
      
      setDiscoveredDevices(prev => {
        if (prev.length >= 12) {
          clearInterval(deviceInterval);
          return prev;
        }
        return [...prev, randomDeviceIndex];
      });
    }, 800);

    return () => clearInterval(deviceInterval);
  }, [isLoading]);

  if (!isLoading) return null;

  return (
    <div className={cn("flex flex-col items-center justify-center p-8", className)}>
      <div className="mb-8 text-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="text-2xl font-bold mb-2"
        >
          Device Discovery
        </motion.div>
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-muted-foreground"
        >
          {messages[currentStage]}
        </motion.div>
      </div>

      <motion.div 
        className="relative w-48 h-48 mb-8"
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{ 
          duration: 10,
          repeat: Infinity,
          ease: "linear"
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
        </div>
        
        <div className="absolute inset-0">
          {discoveredDevices.map((typeIndex, index) => {
            const device = deviceTypes[typeIndex];
            const DeviceIcon = device.icon;
            const angle = (index * (360 / 12)) * (Math.PI / 180);
            const distance = 70;
            const x = Math.cos(angle) * distance;
            const y = Math.sin(angle) * distance;
            
            return (
              <motion.div
                key={index}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.1, type: 'spring' }}
                className={cn("absolute w-8 h-8 flex items-center justify-center rounded-full bg-card", device.color)}
                style={{
                  left: `calc(50% + ${x}px - 16px)`,
                  top: `calc(50% + ${y}px - 16px)`,
                }}
              >
                <DeviceIcon className="w-5 h-5 text-card-foreground" />
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      <div className="mt-4 space-y-2 w-full max-w-md">
        {[0, 1, 2, 3, 4].map((stage) => (
          <motion.div
            key={stage}
            initial={{ width: "0%" }}
            animate={{ 
              width: currentStage >= stage ? "100%" : "0%",
              opacity: currentStage >= stage ? 1 : 0.3
            }}
            transition={{ duration: 0.5 }}
            className={cn(
              "h-1 bg-primary rounded-full",
              currentStage > stage ? "bg-primary" : "bg-muted"
            )}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ 
          opacity: discoveredDevices.length >= 8 ? 1 : 0 
        }}
        transition={{ duration: 0.5 }}
        className="mt-6 text-sm text-muted-foreground"
      >
        Discovered {discoveredDevices.length} devices...
      </motion.div>
    </div>
  );
}